import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Badge, Button, Alert, Table } from "react-bootstrap";
import { FaChevronLeft, FaTruck, FaExternalLinkAlt } from "react-icons/fa";
import { apiFetch } from "./utils/auth";
import AttachmentsButton from "./components/ReferenceDocsPanel";
import OrderChainPanel from "./components/OrderChainPanel";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// ── Formatting helpers (mirrors OrderView) ──────────────────────────────────
function money(v, symbol) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
  return symbol ? `${symbol}${n}` : n;
}
function fmtQty(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(Number(v));
}
function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Status text/badge mirror the Shipping search grid so the two never disagree.
function statusText(h) {
  if (h.voided) return "Voided";
  const st = (h.shipStatus || "").toLowerCase();
  if (st.includes("invoic")) return "Invoiced";
  if (st.includes("ship"))   return "Shipped";
  if (st.includes("stage"))  return "Staged";
  if (st.includes("close"))  return "Closed";
  if (st.includes("open") || !h.shipStatus) return "Open";
  return h.shipStatus;
}
function StatusBadge({ h }) {
  const text = statusText(h);
  const bg =
    text === "Voided" ? "danger"
    : text === "Invoiced" || text === "Shipped" ? "success"
    : text === "Staged" ? "warning"
    : text === "Closed" ? "secondary"
    : "primary";
  return <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>{text}</Badge>;
}

// ── Small presentational bits (same shapes as OrderView) ────────────────────
function Card({ title, right, children, style }) {
  return (
    <div className="qw-card-flat qw-pad" style={{ marginBottom: 14, ...style }}>
      {(title || right) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {title && <span className="qw-eyebrow">{title}</span>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="qw-eyebrow" style={{ marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: "#0F172A", whiteSpace: "pre-wrap" }}>{children || "—"}</div>
    </div>
  );
}

function TrackingLink({ number, url }) {
  if (!number) return <span style={{ color: "#CBD5E1" }}>—</span>;
  if (!url) return <span className="qw-mono">{number}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="qw-mono" title="Track with the carrier">
      {number} <FaExternalLinkAlt size={10} style={{ marginLeft: 4, verticalAlign: "baseline" }} />
    </a>
  );
}

// =============================================================================
// ShipmentView — read-only shipment (pack) detail page, reached from the
// Shipping search row and the quote/order expansion pack chips. One live GET
// (/api/shipments/{packNum}?include=order) powers header, lines, tracking,
// misc charges, and the originating-order summary. Strictly view-only: no
// mutations of any kind.
// =============================================================================

function ShipmentView() {
  const { packNum } = useParams();
  const navigate = useNavigate();

  // Related-records disclosure. Collapsed by default: the panel behind it
  // makes a live Epicor call, and that must never be on the page-open path.
  const [chainOpen, setChainOpen] = useState(false);

  const [data, setData]       = useState(null);   // ShipmentFullDTO
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/shipments/${encodeURIComponent(packNum)}?include=order`);
      const json = await r.json().catch(() => null);
      if (!r.ok || !json?.success || !json?.shipment) {
        setError(json?.error || json?.message || `Shipment #${packNum} not found (HTTP ${r.status}).`);
        setData(null);
      } else {
        setData(json.shipment);
      }
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [packNum]);

  useEffect(() => { load(); }, [load]);

  const h        = data?.header || {};
  const lines    = data?.lines || [];
  const tracking = data?.tracking || null;
  const miscs    = data?.miscCharges || [];
  const order    = data?.linkedOrder || null;
  const symbol   = h.currencyCodeCurrSymbol || "$";

  // MiscShip packs carry an inline address; CustShip packs a formatted one.
  const shipToAddress =
    h.shipToAddressFormatted ||
    [h.miscShipName, h.miscShipAddress1, h.miscShipAddress2, h.miscShipAddress3,
     [h.miscShipCity, h.miscShipState, h.miscShipZIP].filter(Boolean).join(", "),
     h.miscShipCountry].filter(Boolean).join("\n");

  const linesTotal = lines.reduce((sum, l) => sum + (Number(l.docExtPrice) || 0), 0);

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaTruck size={18} /></div>
          <div>
            <div className="qw-header-title">Shipment — Pack #{packNum}</div>
            <div className="qw-header-sub">{h.customerName || h.customerCustID || "Customer shipment"}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {h.orderNum > 0 && (
            <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
              onClick={() => navigate(`/order/${h.orderNum}`)}>
              Order #{h.orderNum}
            </Button>
          )}
          {/* Reference docs (notes/photos/files) — anchored to the pack number
              with entityType "Shipment" (the API's name for pack slips).
              Gates itself on 00060. */}
          <AttachmentsButton entityType="Shipment" entityId={packNum} />
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }}
            onClick={() => navigate(-1)}>
            <FaChevronLeft style={{ marginRight: 6 }} /> Back
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading shipment…</div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>
      ) : (
        <>
          {/* Status badges */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <StatusBadge h={h} />
            {h.invoiced && statusText(h) !== "Invoiced" && <Badge bg="info">Invoiced</Badge>}
            {!h.invoiced && h.readyToInvoice && <span className="qw-pill qw-pill-slate">Ready to invoice</span>}
            {h.creditHold && <Badge bg="danger">Credit hold</Badge>}
            {h.shipmentSource === "MiscShip" && <span className="qw-pill qw-pill-slate">Misc shipment</span>}
            {(h.hazmat || h.hazardousShipment) && <Badge bg="warning" text="dark">Hazmat</Badge>}
            {h.intrntlShip && <span className="qw-pill qw-pill-indigo">International</span>}
          </div>

          {/* Summary + carrier/tracking */}
          <div className="qw-grid-2" style={{ marginBottom: 14 }}>
            <Card title="Shipment summary">
              <Field label="Customer">{h.customerName} {h.customerCustID ? `(${h.customerCustID})` : ""}</Field>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 120px" }}><Field label="Ship date">{fmtDate(h.shipDate)}</Field></div>
                <div style={{ flex: "1 1 120px" }}>
                  <Field label="Order">
                    {h.orderNum > 0 ? (
                      <a href={`/order/${h.orderNum}`} onClick={(e) => { e.preventDefault(); navigate(`/order/${h.orderNum}`); }} style={{ fontWeight: 600, color: "#0052A5" }}>
                        #{h.orderNum}
                      </a>
                    ) : "—"}
                    {order ? <span style={{ color: "#64748B" }}> · {fmtDate(order.orderDate)} · {money(order.docOrderAmt, symbol)}</span> : null}
                  </Field>
                </div>
                <div style={{ flex: "1 1 120px" }}><Field label="Plant">{h.plantName || h.plant}</Field></div>
              </div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 120px" }}><Field label="Shipped by">{h.shipPerson || h.entryPerson}</Field></div>
                <div style={{ flex: "1 1 120px" }}><Field label="Sales rep">{h.salesRepList}</Field></div>
                <div style={{ flex: "1 1 120px" }}>
                  <Field label="Weight">{h.weight ? `${fmtQty(h.weight)} ${h.weightUOM || ""}` : "—"}</Field>
                </div>
              </div>
              {h.shipComment && <Field label="Comment">{h.shipComment}</Field>}
            </Card>

            <Card title="Carrier & tracking">
              <Field label="Ship via">{h.shipViaCodeDescription || h.shipViaCode}</Field>
              <Field label="Tracking">
                <TrackingLink
                  number={tracking?.primaryTrackingNumber || h.trackingNumber}
                  url={tracking?.outTrackingLink_c || tracking?.trackingUrl || h.outTrackingLink_c}
                />
              </Field>
              {(tracking?.cartons || []).length > 0 && (
                <>
                  <div className="qw-eyebrow" style={{ margin: "8px 0 4px" }}>Cartons</div>
                  {tracking.cartons.map((c) => (
                    <div key={c.caseNum} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5, padding: "3px 0", borderBottom: "1px dashed #F1F5F9" }}>
                      <span style={{ color: "#64748B" }}>{c.caseID || `Case ${c.caseNum}`}</span>
                      <TrackingLink number={c.trackingNumber} url={c.trackingUrl} />
                      <span style={{ color: "#94A3B8" }}>{c.pkgWeight ? `${fmtQty(c.pkgWeight)} ${c.manifestWtUOM || ""}` : ""}</span>
                    </div>
                  ))}
                </>
              )}
              {h.wayBillNbr && <Field label="Waybill">{h.wayBillNbr}</Field>}
            </Card>
          </div>

          {/* Addresses */}
          <div className="qw-grid-2" style={{ marginBottom: 14 }}>
            <Card title="Ship to">
              <Field label={h.shipToNumName || "Ship-to"}>{shipToAddress}</Field>
              {h.miscShipPhone && <div style={{ fontSize: 12.5, color: "#64748B" }}>{h.miscShipPhone}</div>}
            </Card>
            <Card title="Sold to">
              <Field label={h.customerBTName || h.btCustomerName || h.customerName || "Sold-to"}>{h.soldToAddressFormatted}</Field>
            </Card>
          </div>

          {/* Related records — the rest of this order's chain. Collapsed, and
              the panel is only mounted once opened, so the shipment page costs
              exactly what it did before. */}
          {h.orderNum > 0 && (
            <Card title="Related records" right={
              <button type="button" className="qw-btn qw-btn-ghost qw-btn-sm"
                      onClick={() => setChainOpen((v) => !v)}>
                {chainOpen ? "Hide" : "Show"}
              </button>
            } style={{ marginBottom: 14 }}>
              {chainOpen
                ? <OrderChainPanel orderNum={h.orderNum} navigate={navigate}
                                     hereKind="shipment" hereRef={packNum} bare />
                : <div style={{ fontSize: 12.5, color: "#64748B" }}>
                    The quote, invoices and payments linked to order #{h.orderNum}.
                  </div>}
            </Card>
          )}

          {/* Lines */}
          <Card title={`Lines (${lines.length})`}>
            {lines.length === 0 ? (
              <div style={{ color: "#94A3B8", fontSize: 13 }}>No lines on this pack.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" hover style={{ fontSize: 13, minWidth: 720 }}>
                  <thead>
                    <tr style={{ color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                      <th>Line</th>
                      <th>Part</th>
                      <th>Description</th>
                      <th className="text-end">Shipped qty</th>
                      <th>UOM</th>
                      <th className="text-end">Unit price</th>
                      <th className="text-end">Ext price</th>
                      <th>Order line</th>
                      <th>Lot / Bin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.packLine}>
                        <td>{l.packLine}</td>
                        <td className="fw-semibold">{l.partNum || "—"}</td>
                        <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.lineDesc || l.partNumPartDescription || ""}>
                          {l.lineDesc || l.partNumPartDescription || "—"}
                        </td>
                        <td className="text-end qw-mono">{fmtQty(l.shippedQty)}</td>
                        <td>{l.salesUM || l.ium || "—"}</td>
                        <td className="text-end qw-mono">{money(l.docUnitPrice, symbol)}</td>
                        <td className="text-end qw-mono">{money(l.docExtPrice, symbol)}</td>
                        <td>{l.orderNum > 0 ? `${l.orderNum} / ${l.orderLine}` : "—"}</td>
                        <td style={{ color: "#64748B" }}>{[l.lotNum, l.binNum].filter(Boolean).join(" / ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={6} className="text-end">Lines total</td>
                      <td className="text-end qw-mono" style={{ color: "#0052A5" }}>{money(linesTotal, symbol)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </Card>

          {/* Misc charges — freight/handling etc., only when present */}
          {miscs.length > 0 && (
            <Card title="Misc charges">
              {miscs.map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                  <span style={{ color: "#64748B" }}>{m.description || m.miscCode || "Charge"}</span>
                  <span className="qw-mono">{money(m.docMiscAmt ?? m.miscAmt, symbol)}</span>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default ShipmentView;
